import React, {useEffect, useState, useContext} from 'react';
import axios from 'axios';
import API_BASE_URL from '../../utils/apiBaseUrl';
import './PastChampions.css';
import { AccountContext } from './../../components/UserPool/Account';
import { TrophyFill, Bullseye } from 'react-bootstrap-icons';
import AlertModal from '../../components/AlertModal/AlertModal';

// The pool's first season. Bump this only if the league ever predates it --
// unlike the year list, this never needs a yearly edit.
const FIRST_SEASON_YEAR = 2022;

// A season isn't "past" (i.e. has a real champion) until it's actually over.
// Rather than hand-maintain a cutoff every year, treat every year strictly
// before the current calendar year as concluded -- NFL seasons run Sept
// through early Jan/Feb, so by the time the calendar rolls to a new year the
// previous season's regular season (and this pool, which is decided by the
// end of the regular season) has already wrapped. This is also double-checked
// per-year below against real standings data, in case a "concluded" year
// still has no data for some other reason.
function lastConcludedSeasonYear() {
    return new Date().getFullYear() - 1;
}

// Every user tied for the top value in a standings list (ties really do
// happen and already split payouts in the real rules -- see Rules.js).
function topEntries(list, getValue) {
    if (!list || list.length === 0) return { names: [], value: 0 };
    const topValue = getValue(list[0]);
    if (!topValue) return { names: [], value: 0 };
    const names = list.filter((entry) => getValue(entry) === topValue).map((entry) => Object.keys(entry)[0]);
    return { names, value: topValue };
}

function PastChampions() {
    const { getSession } = useContext(AccountContext);
    const [newSession, setNewSession] = useState(undefined);
    const [champions, setChampions] = useState(null); // null = loading
    const [showAlertModal, setShowAlertModal] = useState([false, '', '']);

    useEffect(() => {
        getSession(setNewSession)
          .then((session) => {
            console.log('Session Activated');
          })
          .catch((err) => {
            console.log('Session: ', err);
          });
      }, []);

    useEffect(() => {
        if (newSession === undefined) return;

        const endYear = lastConcludedSeasonYear();
        const years = [];
        for (let y = endYear; y >= FIRST_SEASON_YEAR; y--) {
            years.push(String(y));
        }

        Promise.all(
            years.map((year) =>
                axios.get(`${API_BASE_URL}/standings`, {
                    headers: {
                        Authorization: newSession['idToken']['jwtToken'],
                        'Content-Type': 'application/json'
                    },
                    params: { year }
                }).then((response) => ({ year, data: response['data'] }))
                  .catch(() => ({ year, data: null }))
            )
        ).then((results) => {
            const withChamps = results
                .map(({ year, data }) => {
                    if (!data) return null;
                    const streak = topEntries(data['longest_start_streak'], (entry) => Object.values(entry)[0][0]);
                    const correct = topEntries(data['most_correct'], (entry) => Object.values(entry)[0]);
                    if (streak.names.length === 0 && correct.names.length === 0) return null;
                    return { year, streak, correct };
                })
                .filter(Boolean);
            setChampions(withChamps);
        }).catch((error) => {
            console.log(error);
            setChampions([]);
            setShowAlertModal([true, 'Unable to load past champions. Refresh the page and try again.', 'We\'ve hit a snag']);
        });
      }, [newSession]);

    return (
        <div className='past-champions-page'>
            <div className='page-header'>
                <h1 className='page-title'>Past Champions</h1>
                <p className='page-subtitle'>Longest start streak and most correct, every season</p>
            </div>
            <div className='past-champions-section'>
                {
                    champions === null
                        ? Array.from({length: 3}).map((_, i) => (
                            <div key={'skeleton-' + i} className='champion-year-card'>
                                <div className='skeleton champions-skeleton-year' />
                                <div className='champion-categories'>
                                    <div className='skeleton champions-skeleton-row' />
                                    <div className='skeleton champions-skeleton-row' />
                                </div>
                            </div>
                        ))
                        : champions.length === 0
                            ? <p className='past-champions-empty'>No completed seasons yet -- check back once a season wraps up.</p>
                            : champions.map(({year, streak, correct}) => (
                                <div key={year} className='champion-year-card'>
                                    <div className='champion-year'>{year}</div>
                                    <div className='champion-categories'>
                                        <div className='champion-category'>
                                            <div className='champion-category-label'>
                                                <TrophyFill className='champion-category-icon streak' />
                                                Longest Start Streak
                                            </div>
                                            <div className='champion-names'>{streak.names.join(', ')}</div>
                                            <div className='champion-value'>{streak.value} correct picks to start the season</div>
                                        </div>
                                        <div className='champion-category'>
                                            <div className='champion-category-label'>
                                                <Bullseye className='champion-category-icon correct' />
                                                Most Correct
                                            </div>
                                            <div className='champion-names'>{correct.names.join(', ')}</div>
                                            <div className='champion-value'>{correct.value} correct picks total</div>
                                        </div>
                                    </div>
                                </div>
                            ))
                }
            </div>
            <AlertModal
                show={showAlertModal[0]}
                onHide={()=>{setShowAlertModal([false, '', ''])}}
                modalTitle={showAlertModal[2]}
                modalBody={showAlertModal[1]}
            />
        </div>
    );
}

export default PastChampions;
